import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import * as solidIcons from '@fortawesome/free-solid-svg-icons';
import { useBusinessData } from '../../context/BusinessContext';
import '../../css/CareerDetail.css';

const CareerDetail = () => {
  const { jobId } = useParams();
  const navigate = useNavigate();
  const { loading, error, getData, hasData } = useBusinessData();
  const [job, setJob] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    coverLetter: '',
    resume: null,
    agreeTerms: false
  });
  const [formErrors, setFormErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  
  // Find the job based on the ID parameter
  useEffect(() => {
    if (!loading && !error) {
      const allJobs = getData('careers.openPositions', []);
      const foundJob = allJobs.find((j, index) => index.toString() === jobId);
      
      if (foundJob) {
        setJob(foundJob);
      } else {
        // Job not found, redirect to About page
        navigate('/about');
      }
    }
  }, [loading, error, getData, jobId, navigate]);
  
  // Handle form field changes
  const handleChange = (e) => {
    const { name, value, type, checked, files } = e.target;
    
    if (type === 'file') {
      setFormData({
        ...formData,
        [name]: files[0]
      });
    } else if (type === 'checkbox') {
      setFormData({
        ...formData,
        [name]: checked
      });
    } else {
      setFormData({
        ...formData,
        [name]: value
      });
    }
    
    // Clear error for this field when user types
    if (formErrors[name]) {
      setFormErrors({
        ...formErrors,
        [name]: null
      });
    }
  };
  
  // Validate form
  const validateForm = () => {
    const errors = {};
    
    if (!formData.name.trim()) {
      errors.name = 'Name is required';
    }
    
    if (!formData.email.trim()) {
      errors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      errors.email = 'Email is invalid';
    }
    
    if (!formData.phone.trim()) {
      errors.phone = 'Phone number is required';
    }
    
    if (!formData.resume) {
      errors.resume = 'Resume is required';
    }
    
    if (!formData.agreeTerms) {
      errors.agreeTerms = 'You must agree to the terms';
    }
    
    return errors;
  };
  
  // Handle form submission
  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Validate form
    const errors = validateForm();
    
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }
    
    // Simulate form submission
    setIsSubmitting(true);
    
    // Simulate API call
    setTimeout(() => {
      setIsSubmitting(false);
      setSubmitSuccess(true);
      
      // Reset form
      setFormData({
        name: '',
        email: '',
        phone: '',
        coverLetter: '',
        resume: null,
        agreeTerms: false
      });
    }, 1500);
  };
  
  if (loading) {
    return <div className="loading">Loading job details...</div>;
  }
  
  if (error) {
    return <div className="error">Error loading job details: {error}</div>;
  }
  
  if (!job) {
    return <div className="loading">Job not found</div>;
  }
  
  return (
    <div className="career-detail-page">
      {/* Job Hero Section */}
      <div className="job-hero">
        <div className="container">
          <Link to="/about" className="back-link">
            &larr; Back to Careers
          </Link>
          <h1>{job.title}</h1>
          <div className="job-meta">
            <span className="job-meta-item">
              <FontAwesomeIcon icon={solidIcons.faBuilding} /> {job.department}
            </span>
            <span className="job-meta-item">
              <FontAwesomeIcon icon={solidIcons.faMapMarkerAlt} /> {job.location}
            </span>
            <span className="job-meta-item">
              <FontAwesomeIcon icon={solidIcons.faClock} /> {job.type}
            </span>
          </div>
        </div>
      </div>
      
      <div className="container">
        <div className="job-detail-content">
          <div className="job-description-section">
            {/* Job Overview */}
            <div className="job-overview">
              <h2>Job Overview</h2>
              <p>{job.description}</p>
            </div>
            
            {/* Requirements Section */}
            <div className="job-requirements">
              <h2>Requirements</h2>
              <ul className="requirements-list">
                {job.requirements.map((req, index) => (
                  <li key={index}>
                    <FontAwesomeIcon icon={solidIcons.faCheck} className="req-icon" />
                    <span>{req}</span>
                  </li>
                ))}
              </ul>
            </div>
            
            {/* Benefits Section */}
            <div className="job-benefits">
              <h2>Benefits</h2>
              <ul className="benefits-list">
                {job.benefits.map((benefit, index) => (
                  <li key={index}>
                    <FontAwesomeIcon icon={solidIcons.faCheck} className="benefit-icon" />
                    <span>{benefit}</span>
                  </li>
                ))}
              </ul>
            </div>
            
            {/* General Company Benefits */}
            <div className="company-benefits">
              <h2>Why Join Us</h2>
              <ul className="company-benefits-list">
                {getData('careers.benefits', []).map((benefit, index) => (
                  <li key={index}>
                    <FontAwesomeIcon icon={solidIcons.faCheck} className="benefit-icon" />
                    <span>{benefit}</span>
                  </li>
                ))}
              </ul>
              <p className="company-culture">{getData('careers.culture', '')}</p>
            </div>
          </div>
          
          {/* Application Form */}
          <div className="application-form-section">
            <h2>Apply for this Position</h2>
            
            {submitSuccess ? (
              <div className="success-message">
                <h3>Application Submitted!</h3>
                <p>Thank you for your interest in joining our team. We will review your application and get back to you soon.</p>
                <button 
                  className="new-application-btn"
                  onClick={() => setSubmitSuccess(false)}
                >
                  Submit Another Application
                </button>
              </div>
            ) : (
              <form className="application-form" onSubmit={handleSubmit}>
                <div className="form-group">
                  <label htmlFor="name">Full Name</label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    className={formErrors.name ? 'error' : ''}
                  />
                  {formErrors.name && <span className="error-message">{formErrors.name}</span>}
                </div>
                
                <div className="form-group">
                  <label htmlFor="email">Email Address</label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    className={formErrors.email ? 'error' : ''}
                  />
                  {formErrors.email && <span className="error-message">{formErrors.email}</span>}
                </div>
                
                <div className="form-group">
                  <label htmlFor="phone">Phone Number</label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    className={formErrors.phone ? 'error' : ''}
                  />
                  {formErrors.phone && <span className="error-message">{formErrors.phone}</span>}
                </div>
                
                <div className="form-group">
                  <label htmlFor="coverLetter">Cover Letter (Optional)</label>
                  <textarea
                    id="coverLetter"
                    name="coverLetter"
                    value={formData.coverLetter}
                    onChange={handleChange}
                    rows="5"
                  ></textarea>
                </div>
                
                <div className="form-group">
                  <label htmlFor="resume">Resume (PDF, DOC, DOCX)</label>
                  <div className="file-input-wrapper">
                    <input
                      type="file"
                      id="resume"
                      name="resume"
                      onChange={handleChange}
                      accept=".pdf,.doc,.docx"
                      className={formErrors.resume ? 'error' : ''}
                    />
                    <div className="file-input-placeholder">
                      {formData.resume ? formData.resume.name : 'Choose file...'}
                    </div>
                  </div>
                  {formErrors.resume && <span className="error-message">{formErrors.resume}</span>}
                </div>
                
                <div className="form-group checkbox-group">
                  <input
                    type="checkbox"
                    id="agreeTerms"
                    name="agreeTerms"
                    checked={formData.agreeTerms}
                    onChange={handleChange}
                    className={formErrors.agreeTerms ? 'error' : ''}
                  />
                  <label htmlFor="agreeTerms">
                    I agree to the processing of my personal data for recruitment purposes
                  </label>
                  {formErrors.agreeTerms && <span className="error-message">{formErrors.agreeTerms}</span>}
                </div>
                
                <button 
                  type="submit" 
                  className="submit-btn"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? 'Submitting...' : 'Submit Application'}
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CareerDetail; 